"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Language } from "@/hooks/useLanguage";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import {
	FOUNDATION_PRINCIPAL,
	type SponsoredCopyConfig,
	type SponsoredCopyItem,
	type SponsoredHolding,
	type SponsoredRankingResponse,
} from "../types";

const uiLog = createLogAdapter(
	"app/plaza/components/sponsored-ranking-banner/hooks/use-sponsored-ranking.ts",
);

const REFRESH_INTERVAL = 60 * 1000;
const SPONSORED_RANKING_CACHE_KEY = "plaza-sponsored-ranking-cache-v2";
const SPONSORED_RANKING_CACHE_TTL_MS = 5 * 60 * 1000;
const SUPPORT_AGE_TOKEN_LIMIT = 10;

function readCachedHoldings(): SponsoredHolding[] {
	if (typeof window === "undefined") return [];

	try {
		const raw = window.localStorage.getItem(SPONSORED_RANKING_CACHE_KEY);
		if (!raw) return [];

		const parsed = JSON.parse(raw) as {
			cachedAt?: number;
			holdings?: SponsoredHolding[];
		};

		if (!parsed.cachedAt || Date.now() - parsed.cachedAt > SPONSORED_RANKING_CACHE_TTL_MS) {
			window.localStorage.removeItem(SPONSORED_RANKING_CACHE_KEY);
			return [];
		}

		return Array.isArray(parsed.holdings) ? parsed.holdings : [];
	} catch (error) {
		uiLog.warn("Failed to read sponsored ranking cache", error);
		return [];
	}
}

function writeCachedHoldings(holdings: SponsoredHolding[]) {
	if (typeof window === "undefined") return;

	try {
		window.localStorage.setItem(
			SPONSORED_RANKING_CACHE_KEY,
			JSON.stringify({
				cachedAt: Date.now(),
				holdings,
			}),
		);
	} catch (error) {
		uiLog.warn("Failed to persist sponsored ranking cache", error);
	}
}

function normalizeCopyValue(value?: string): string | undefined {
	if (typeof value !== "string") return undefined;
	const normalized = value.trim();
	return normalized ? normalized : undefined;
}

function resolveTokenCopy(tokenId: string, copyItems: SponsoredCopyItem[]) {
	const matched = copyItems.find((item) => item?.tokenId === tokenId && item.enabled !== false);
	if (!matched) return {};

	return {
		customCopyZh: normalizeCopyValue(matched.copy),
		customCopyEn: normalizeCopyValue(matched.copyEn),
	};
}

async function fetchSponsoredCopyConfig(signal: AbortSignal): Promise<SponsoredCopyConfig> {
	const response = await fetch("/api/plaza/sponsored-ranking-copy", {
		cache: "no-store",
		signal,
	});

	if (!response.ok) {
		throw new Error(`Copy config HTTP ${response.status}`);
	}

	return await response.json();
}

async function fetchSupportAgeMap(
	tokenIds: string[],
	signal: AbortSignal,
): Promise<Map<string, number>> {
	if (!tokenIds.length) return new Map();

	const response = await fetch("/api/plaza/sponsored-ranking-support-age", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		cache: "no-store",
		signal,
		body: JSON.stringify({ tokenIds }),
	});

	if (!response.ok) {
		throw new Error(`Support age HTTP ${response.status}`);
	}

	const payload = (await response.json()) as {
		data?: Array<{ tokenId?: string; supportAgeDays?: number }>;
	};

	return new Map(
		(payload.data ?? [])
			.filter(
				(item): item is { tokenId: string; supportAgeDays: number } =>
					typeof item?.tokenId === "string" &&
					typeof item?.supportAgeDays === "number" &&
					Number.isFinite(item.supportAgeDays),
			)
			.map((item) => [item.tokenId, item.supportAgeDays]),
	);
}

function mergeHoldingsWithCopy(holdings: SponsoredHolding[], copyItems: SponsoredCopyItem[]) {
	return holdings.map((holding) => ({
		...holding,
		token: {
			...holding.token,
			...resolveTokenCopy(holding.token.id, copyItems),
		},
	}));
}

const EMPTY_COPY_CONFIG: SponsoredCopyConfig = {
	version: 1,
	updatedAt: new Date(0).toISOString(),
	items: [],
};

export function useSponsoredRanking(language: Language) {
	const [holdings, setHoldings] = useState<SponsoredHolding[]>(() => readCachedHoldings());
	const [loading, setLoading] = useState(() => holdings.length === 0);
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	const load = useCallback(async (isBackgroundRefresh = false) => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;

		if (!isBackgroundRefresh) {
			setLoading(true);
		}

		try {
			const [response, copyConfig] = await Promise.all([
				fetch(
					`https://api.odin.fun/v1/user/${FOUNDATION_PRINCIPAL}/tokens?page=1&limit=12&sort=holding_value%3Adesc`,
					{
						cache: "no-store",
						signal: controller.signal,
					},
				),
				fetchSponsoredCopyConfig(controller.signal).catch((configError) => {
					uiLog.warn("Failed to load sponsored ranking copy config", configError);
					return EMPTY_COPY_CONFIG;
				}),
			]);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			const result: SponsoredRankingResponse = await response.json();
			const filtered = (result.data || [])
				.filter((item) => item?.token?.id && !["2rfi", "btc"].includes(item.token.id))
				.slice(0, 10);
			const merged = mergeHoldingsWithCopy(filtered, copyConfig.items);
			const topTokenIds = merged
				.slice(0, SUPPORT_AGE_TOKEN_LIMIT)
				.map((holding) => holding.token.id);
			const supportAgeMap = await fetchSupportAgeMap(topTokenIds, controller.signal).catch(
				(supportAgeError) => {
					uiLog.warn("Failed to load sponsored ranking support age", supportAgeError);
					return new Map<string, number>();
				},
			);
			const enriched = merged.map((holding, index) => ({
				...holding,
				supportAgeDays:
					index < SUPPORT_AGE_TOKEN_LIMIT
						? supportAgeMap.get(holding.token.id) ?? 10
						: undefined,
			}));

			setHoldings(enriched);
			writeCachedHoldings(enriched);
			setError(null);
		} catch (error) {
			if ((error as Error).name === "AbortError") return;
			uiLog.error("Failed to load sponsored ranking holdings", error);
			setError((error as Error).message || "load_failed");
		} finally {
			if (!isBackgroundRefresh) {
				setLoading(false);
			}
		}
	}, []);

	useEffect(() => {
		void language;
		load();

		const interval = window.setInterval(() => {
			load(true);
		}, REFRESH_INTERVAL);

		return () => {
			window.clearInterval(interval);
			abortRef.current?.abort();
		};
	}, [language, load]);

	return {
		holdings,
		loading,
		error,
		reload: load,
	};
}
